import type { LayerState } from '@vitals/shared';
import { delaysVital } from './serviceAlerts.js';
import { weatherVital } from './weather.js';
import { tidesVital } from './tides.js';
import { gridVital } from './grid.js';
import { bikesVital } from './citibike.js';
import { complaintsVital } from './c311.js';
import { ferriesVital } from './ferry.js';
import { alertsVital } from './nws.js';
import { speedVital } from './dotSpeeds.js';
import { busesVital } from './buses.js';
import { aqiVital } from './aqi.js';
import { floodVital } from './floodnet.js';
import { eneVital } from './ene.js';
import { planesVital } from './planes.js';
import { helicoptersVital } from './helicopters.js';
import { powerOutagesVital } from './powerOutages.js';
import { harborVital } from './harbor.js';
import { commuterRailCluster } from './commuterRail.js';
import { vitalIcon } from './icons.js';
import { subwayVital } from './subway.js';

/** Semantic status of a vital, driving its accent color. */
export type VitalTone = 'good' | 'warn' | 'bad';

/** Which group a vital belongs to in the board layout. */
export type VitalGroup = 'transit' | 'weather' | 'city';

export type VitalDetail =
  | { kind: 'routes'; routes: string[]; text: string }
  | { kind: 'metric'; label: string; value: string; elapsed?: boolean }
  | { kind: 'alert'; title: string; meta?: string }
  | { kind: 'bar'; label: string; value: string; frac: number }
  | { kind: 'category'; label: string; value: string; percent: string; frac: number; color?: string }
  | { kind: 'note'; text: string };

/** Display model for a single vital cell in the panel strip. */
export interface VitalView {
  value: string;
  label: string;
  down: boolean;
  stale: boolean;
  /** Optional semantic accent (e.g. green "all clear", amber "delays"). */
  tone?: VitalTone;
  /** Optional route IDs rendered as MTA bullets in place of the meta line. */
  bullets?: string[];
  /** Optional heading and structured rows shown in the chip's detail card. */
  detailTitle?: string;
  details?: VitalDetail[];
}

/**
 * Core vital definition used by individual vital modules.
 * Does not include board-level `group` or `icon` — those are added in VITALS.
 */
export interface VitalDef {
  id: string;
  layerId: string;
  select: (s: LayerState | undefined) => VitalView;
}

/** Full vital registration with board layout metadata. */
export interface Vital extends VitalDef {
  /** Static group for board layout. */
  group: VitalGroup;
  /** Static inline SVG icon string (trusted constant, never feed-derived). */
  icon: string;
}

/**
 * A group of related vitals rendered as one collapsible chip: collapsed it
 * shows an aggregate view; expanded it reveals its children as normal chips.
 * `layerIds` mirrors children order and drives freshness.
 */
export interface VitalCluster {
  id: string;
  group: VitalGroup;
  icon: string;
  layerIds: string[];
  children: Vital[];
  select: (states: (LayerState | undefined)[]) => VitalView;
}

/** An entry in the VITALS registry: a single chip or a collapsible cluster. */
export type VitalEntry = Vital | VitalCluster;

export function isCluster(entry: VitalEntry): entry is VitalCluster {
  return 'children' in entry;
}

/** Active subway trains, or null if the feed is down/missing (shows "—"). */
export function activeTrains(state: LayerState | undefined): number | null {
  if (!state || state.status === 'down') return null;
  return state.features.length;
}

/** Registered vitals. Phase B/C/D append entries here. */
export const VITALS: VitalEntry[] = [
  { ...subwayVital, group: 'transit', icon: vitalIcon('subway') },
  commuterRailCluster,
  { ...delaysVital, group: 'transit' as VitalGroup, icon: vitalIcon('delays') },
  { ...busesVital, group: 'transit' as VitalGroup, icon: vitalIcon('buses') },
  { ...eneVital, group: 'transit' as VitalGroup, icon: vitalIcon('ene') },
  { ...ferriesVital, group: 'transit' as VitalGroup, icon: vitalIcon('ferries') },
  // Ships (AIS) chip hidden for launch — the feed isn't reliable yet. The map
  // layer and aisVital itself are untouched; re-add this entry to restore it.
  // { ...aisVital, group: 'transit' as VitalGroup, icon: vitalIcon('ais') },
  { ...planesVital, group: 'transit' as VitalGroup, icon: vitalIcon('planes') },
  { ...helicoptersVital, group: 'transit' as VitalGroup, icon: vitalIcon('helicopters') },
  { ...bikesVital, group: 'transit' as VitalGroup, icon: vitalIcon('bikes') },
  { ...weatherVital, group: 'weather' as VitalGroup, icon: vitalIcon('weather') },
  { ...tidesVital, group: 'weather' as VitalGroup, icon: vitalIcon('tide') },
  { ...alertsVital, group: 'weather' as VitalGroup, icon: vitalIcon('alerts') },
  { ...aqiVital, group: 'weather' as VitalGroup, icon: vitalIcon('aqi') },
  { ...floodVital, group: 'weather' as VitalGroup, icon: vitalIcon('flood') },
  gridVital,
  { ...complaintsVital, group: 'city' as VitalGroup, icon: vitalIcon('311') },
  { ...speedVital, group: 'city' as VitalGroup, icon: vitalIcon('speed') },
  { ...powerOutagesVital, group: 'city' as VitalGroup, icon: vitalIcon('power') },
  { ...harborVital, group: 'weather' as VitalGroup, icon: vitalIcon('harbor') },
];
